import { hexToRgb, getLuminance, safe } from '../utils/contrast.js';
import { t } from '../utils/i18n.js';
import { altAttr, textoAlt } from '../utils/alt-text.js';
import { escapeHtml } from '../utils/dom.js';
import { atributosEncuadre } from '../utils/encuadre.js';

// ── GALLERY ────────────────────────────────────────────────
export const galleryBlock = (rawProps) => {
    const p = safe(rawProps);
    const variant = p.variant || 'v1';
    const title = p.title || 'Nuestra Galería';
    const subtitle = p.subtitle || '';

    const isDark = getLuminance(...hexToRgb(p.bgColor)) < 0.179;
    const btnBg = isDark ? "#ffffff" : "#111827";
    const btnIcon = isDark ? "#111827" : "#ffffff";

    // Respaldo VISUAL cuando el bloque llega sin `gallery_items` (lo pintan estos, pero no se
    // guardan en props hasta que el extractor los materializa). Traen KEYWORD y NO pie de foto,
    // y las dos mitades importan:
    //   · con `image: ''` el render no emitía `unsplash-target` → Unsplash no se llamaba NUNCA
    //     (no es que fallara) y la galería salía como seis `placehold.co` grises;
    //   · los pies eran copy en español HARDCODEADO ("Acabado de lujo") que acababa publicado
    //     en la web de un cliente que nunca lo escribió — justo lo que este builder no hace.
    // Mismas keywords que la plantilla del catálogo (`editor/tools.js`): un mismo bloque no
    // puede verse de dos maneras según por dónde entre. Con el stock APAGADO no se llega aquí:
    // `_fix_galeria_vacia` (backend) materializa los huecos vacíos en props a propósito.
    const defaultItems = [
        { image: '', caption: '', tag: '' },
        { image: '', caption: '', tag: '' },
        { image: '', caption: '', tag: '' },
        { image: '', caption: '', tag: '' },
        { image: '', caption: '', tag: '' },
        { image: '', caption: '', tag: '' },
    ];
    // SANEO de lo que llega en props: `gallery_items` lo escribe el LLM, y un `null`, un número o
    // un string suelto dentro del array hacía reventar el `item.image.startsWith` de más abajo.
    // Eso no dejaba sin galería: `renderPage` construye TODO el HTML en un string y solo entonces
    // lo asigna, así que un renderer que lanza deja la página ENTERA sin pintar. Se normaliza cada
    // elemento a la forma que el bloque espera y se descarta lo que no se pueda salvar.
    const normalizarItem = (it) => {
        if (typeof it === 'string') return { image: it, caption: '', tag: '' };
        if (!it || typeof it !== 'object') return null;
        return {
            ...it,
            image: typeof it.image === 'string' ? it.image : '',
            caption: typeof it.caption === 'string' ? it.caption : '',
            tag: typeof it.tag === 'string' ? it.tag : '',
        };
    };
    const itemsProps = (Array.isArray(p.gallery_items) ? p.gallery_items : [])
        .map(normalizarItem)
        .filter(Boolean);
    const items = itemsProps.length > 0 ? itemsProps : defaultItems;

    // --- Placeholder ---
    const PLACEHOLDER = 'https://placehold.co/800x600/cccccc/999999?text=Imagen';

    // ¿`image` es una KEYWORD por resolver, y no una URL/ruta/data: ya lista? Un solo sitio: lo
    // consultan el `<img>` (para pedirle la foto a Unsplash y nacer en opacity:0) y el PIE de la
    // v2, que se funde con ella. Espejo de `editor/unsplash.js::isResolved`, que es la SSOT de esta
    // heurística; aquí se repite porque un bloque no puede importar de `js/editor/` (rompería la
    // cadena registry→bloque, gotcha #3). Si cambias una, cambia la otra.
    // `typeof v === 'string'` no es adorno: desde que `usaStock` la consulta también con
    // `p.image_keyword` (que llega del JSON del sitio y no pasa por `normalizarItem`), un valor
    // no-string reventaba el `.startsWith` y con él TODO el bloque — la misma clase de fallo que
    // ya se corrigió dentro del array (ver la nota de `normalizarItem`).
    const esKeyword = (v) => typeof v === 'string' && !!v && !v.startsWith('http') && !v.startsWith('/') && !v.startsWith('data:');

    // --- Toolbar de cada item ---
    const itemToolbar = (i) => `
        ${i !== 0 ? `<button class="item-add-btn card-add-left" data-array="gallery_items" data-index="${i}">+</button>` : ''}
        <button class="item-add-btn card-add-right" data-array="gallery_items" data-index="${i}">+</button>
        <div class="item-tools">
            <button class="tool-btn item-drag-handle" title="Mover" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.drag}</button>
            <button class="tool-btn image-picker-btn" data-array="gallery_items" data-index="${i}" title="Cambiar imagen" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.image}</button>
            ${items[i]?.image ? `<button class="tool-btn image-remove-btn" data-array="gallery_items" data-item-index="${i}" title="Quitar imagen" style="background: ${btnBg} !important; color: #fb7185 !important;">${window.icons.imageOff}</button>` : ''}
            <button class="tool-btn item-delete-btn" data-array="gallery_items" data-index="${i}" title="Borrar" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.trash}</button>
        </div>`;

    // Toolbar para slider v2
    const sliderToolbar = (i) => `
        <div class="item-tools">
            <button class="tool-btn item-drag-handle" title="Mover" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.drag}</button>
            <button class="tool-btn image-picker-btn" data-array="gallery_items" data-index="${i}" title="Cambiar imagen" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.image}</button>
            ${items[i]?.image ? `<button class="tool-btn image-remove-btn" data-array="gallery_items" data-item-index="${i}" title="Quitar imagen" style="background: ${btnBg} !important; color: #fb7185 !important;">${window.icons.imageOff}</button>` : ''}
            <button class="tool-btn item-delete-btn" data-array="gallery_items" data-index="${i}" title="Borrar" style="background: ${btnBg} !important; color: ${btnIcon} !important;">${window.icons.trash}</button>
        </div>`;

    // ¿ESTA galería usa fotos de archivo? Evidencia LOCAL, y es la única disponible: `usar_stock`
    // es una opción de GENERACIÓN que no se guarda en el sitio, así que el render no puede
    // consultarla. Y no vale adivinar: cuando el cliente APAGA el stock, `_postprocess_site`
    // vacía TODAS las `image` del array (y `image_keyword`), de modo que "aquí no queda ni una
    // keyword" es exactamente la firma de "no quiero fotos de archivo". Un fallback ciego por el
    // pie de foto se las devolvería, deshaciendo esa decisión suya.
    const usaStock = esKeyword(p.image_keyword) || items.some(it => esKeyword(it.image));

    // Keyword de BÚSQUEDA de un item: la suya, o —solo en modo stock— su pie/etiqueta. Ese
    // respaldo se daba por hecho en varios sitios y no existía: un item con pie pero sin
    // `image` se publicaba como placeholder gris. `textoAlt` aplana el innerHTML del campo
    // editable y lo ESCAPA (va dentro de un atributo), reusando la utilidad de alt-text.js.
    const keywordDe = (item) => item.image || (usaStock ? textoAlt(item.caption, item.tag) : '');

    // --- Render imagen ---
    const renderImg = (item, i = 0) => {
        const keyword = keywordDe(item);
        const imgSrc = keyword || PLACEHOLDER;
        // isUnsplash = es una KEYWORD por resolver (no una URL/ruta/data: ya lista). El guard de '/'
        // es imprescindible: sin él, una imagen local '/media/...' se trataba como keyword y salía
        // como placeholder a opacity:0 (mismo criterio que before-after.js — no dupliques la regla).
        const isUnsplash = esKeyword(keyword);
        // Encuadre POR FOTO (utils/encuadre.js, D-1452): foco + zoom guardados en el item; sin
        // ellos, ni clase ni estilo (lo publicado sale como siempre).
        const enc = atributosEncuadre(item);
        const imgClass = (isUnsplash ? 'unsplash-target gallery-img' : 'gallery-img') + enc.clase;
        // `item.image` se escapa aquí (textoAlt ya escapa el respaldo por pie); `data-unsplash-derivada`
        // marca la keyword que sale del pie: el extractor NO la hornea como foto del item.
        const attrKw = item.image ? escapeHtml(keyword) : keyword;
        const derivada = !item.image && keyword ? ' data-unsplash-derivada="1"' : '';
        const imgAttrs = isUnsplash
            ? `class="${imgClass}" data-unsplash="${attrKw}"${derivada} src="${PLACEHOLDER}"`
            : `class="${imgClass}" src="${imgSrc}"`;
        const imgStyle = (isUnsplash
            ? 'width:100%; height:100%; object-fit:cover; display:block; opacity:0; transition:opacity 0.4s;'
            : 'width:100%; height:100%; object-fit:cover; display:block;') + enc.estilo;
        // Cascada de alt (utils/alt-text.js): alt propio del item → su pie de foto → su
        // etiqueta → la keyword de búsqueda. Antes solo miraba `caption`, así que una foto
        // sin pie (lo normal en la galería v2) se publicaba con alt="".
        return `<img ${imgAttrs} ${altAttr(item.alt, item.caption, item.tag, item.image)} loading="lazy" style="${imgStyle}">`
             + celdaVacia(item, i);
    };

    // Chrome del EDITOR sobre una celda que se quedó sin foto (el cliente apagó el stock, o
    // borró la imagen). Sin él, el hueco es un rectángulo gris idéntico a una foto que falló y
    // nadie sabe que se puede rellenar. Es un `.image-picker-btn` de verdad: así abre el
    // selector por la delegación que YA existe (`editor/catalog.js`), sin un handler nuevo ni
    // un `on*` inline (gotcha nº13). Se emite SIEMPRE marcado `.editor-only` —nunca gateado por
    // `editorMode`—: `compileSiteHtml` publica con el editor todavía activo, así que emitir de
    // más es seguro y emitir de menos no (gotcha nº12).
    const celdaVacia = (item, i) => keywordDe(item) ? '' : `
        <button type="button" class="image-picker-btn gallery-empty-notice editor-only"
                data-array="gallery_items" data-index="${i}" title="Elegir una imagen para esta celda">
            ${window.icons?.image || ''}<span>Sin foto: pulsa para elegir</span>
        </button>`;

    // --- Card de item (masonry / featured) ---
    const renderItem = (item, i, extraStyle = '') => {
        return `
        <div class="gallery-item sortable-item" data-item-index="${i}" data-array-name="gallery_items" style="position: relative; ${extraStyle}">
            ${itemToolbar(i)}
            ${renderImg(item, i)}
            ${item.tag ? `<span class="gallery-tag" data-field="tag" contenteditable="true">${item.tag}</span>` : ''}
            <div class="caption-overlay">
                <span data-field="caption" contenteditable="true" style="color:#fff; font-size:calc(0.95rem * var(--text-scale, 1)); font-weight:600;">${item.caption || ''}</span>
            </div>
        </div>`;
    };

    // --- Encabezado compartido ---
    const header = (title || subtitle) ? `
        <div style="text-align:center; margin-bottom:60px;">
            ${title ? `<h2 data-field="title" contenteditable="true" style="font-size:calc(clamp(2rem,4cqw,3rem) * var(--type-scale, 1)); font-weight:900; margin-bottom:16px;">${title}</h2>` : ''}
            ${subtitle ? `<p data-field="subtitle" contenteditable="true" style="opacity:0.6; font-size:calc(1.1rem * var(--text-scale, 1));">${subtitle}</p>` : ''}
        </div>` : '';

    // --- v1: Masonry (CSS columns) ---
    if (variant === 'v1' || (!variant || variant === '')) {
        return `
    <section id="bloque-gallery" class="reveal variant-${variant}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}';">
        ${header}
        <div class="gallery-masonry">
            ${items.map((item, i) => renderItem(item, i)).join('')}
        </div>
    </section>`;
    }

    // --- v2: Slider / Carousel con flechas, swipe y autoplay ---
    if (variant === 'v2') {
        return `
    <section id="bloque-gallery" class="reveal variant-${variant}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}';">
        <style>
        .editor-active .gallery-v2-splide .splide__track { overflow: visible !important; }
        .editor-active .gallery-editor-grid { display: flex; overflow-x: auto; gap: 20px; padding-bottom: 20px; }
        .editor-active .gallery-editor-grid .splide__slide { flex: 0 0 80%; max-width: 600px; }
        .gallery-v2-splide .splide__track { overflow: hidden !important; }
        .gallery-img-wrapper { width: 100%; aspect-ratio: 16/9; overflow: hidden; border-radius: var(--card-radius, 16px); position: relative; }
        </style>
        ${header}
        <div class="splide gallery-v2-splide" data-splide-per-page="1" data-splide-type="loop" data-splide-autoplay="true" data-splide-gap="1rem">
            <div class="splide__track">
                <div class="splide__list gallery-editor-grid" style="margin:0; padding:0;">
                    ${items.map((item, i) => `
                    <div class="splide__slide sortable-item" data-item-index="${i}" data-array-name="gallery_items" style="position:relative;">
                        ${i !== 0 ? `<button class="item-add-btn card-add-left" data-array="gallery_items" data-index="${i}">+</button>` : ''}
                        <button class="item-add-btn card-add-right" data-array="gallery_items" data-index="${i}">+</button>
                        ${sliderToolbar(i)}
                        <div class="gallery-img-wrapper">
                            ${renderImg(item, i)}
                            ${/* El pie nace invisible SOLO si su foto también, para entrar con ella
                                  en el mismo fundido. Antes era `opacity:0` siempre, y quien lo
                                  revela es `unsplash.js::cargar()`, que recorre ÚNICAMENTE los
                                  `.unsplash-target`: con una imagen ya resuelta (subida, o guardada
                                  tras la primera vez) el `<img>` no lleva esa clase, `cargar()` no
                                  corre para él y el pie se quedaba invisible PARA SIEMPRE. */''}
                            <div class="gallery-slide-caption unsplash-caption" style="opacity:${esKeyword(keywordDe(item)) ? '0' : '1'}; transition:opacity 0.4s;">
                                <span data-field="caption" contenteditable="true">${item.caption || ''}</span>
                            </div>
                        </div>
                    </div>`).join('')}
                </div>
            </div>
        </div>
    </section>`;
    }

    // --- v3: Featured — primera imagen grande izquierda + grid 2x2 derecha ---
    const [featured, ...rest] = items;
    const gridItems = rest.slice(0, 4);
    return `
    <section id="bloque-gallery" class="reveal variant-${variant}" style="--bg:${p.bgColor}; --text:${p.textColor}; --accent:${p.accentColor}; --font:'${p.fontFamily || "Inter"}';">
        ${header}
        <div class="gallery-featured">
            <!-- Imagen destacada -->
            <div style="flex: 3; min-width: min(100%, 300px);">
                ${renderItem(featured, 0, 'height: 100%; min-height: 400px; border-radius: var(--card-radius, 16px); overflow: hidden;')}
            </div>
            <!-- Grid 2x2 -->
            <div style="flex: 2; min-width: min(100%, 260px); display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
                ${gridItems.map((item, i) => renderItem(item, i + 1, 'height: 200px; border-radius: var(--card-radius, 16px); overflow: hidden;')).join('')}
                ${gridItems.length === 0 ? `<div style="grid-column: 1/-1; display:flex; align-items:center; justify-content:center; opacity:0.3; font-size:0.85rem;">${t('gallery_more')}</div>` : ''}
            </div>
        </div>
        ${rest.length > 4 ? `
        <div class="gallery-grid" style="margin-top: 16px;">
            ${rest.slice(4).map((item, i) => renderItem(item, i + 5)).join('')}
        </div>` : ''}
    </section>`;
};
